<template>
  <main class="auto-comp-page">
    <CompanyBreadcrumb :aria-label="$t('general.automatic_competency_definitions')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div class="section-header mb-lg-2 border-0 uk-flex-middle auto-comp-header">
      <div class="section-header-left">
        <h2 class="auto-comp-title">
          <span> {{ $t('general.automatic_competency_definitions') }} ({{ items.total }}) </span>
        </h2>
      </div>
      <div class="section-header-right auto-comp-header-actions">
        <router-link v-if="$hasPermission('create_automaticcompetency')" :to="{ name: 'add.automatic.competency' }">
          <button aria-expanded="false" class="btn btn-default auto-comp-action-btn" type="button">
            <i class="uil-location-arrow"></i> {{ $t('general.define_new_auto_competencies') }}
          </button>
        </router-link>

        <button v-else aria-expanded="false" class="btn btn-default auto-comp-action-btn" type="button" disabled
          :style="{ cursor: 'not-allowed' }" :uk-tooltip="$t('general.no_permission')">
          <i class="uil-location-arrow"></i> {{ $t('general.define_new_auto_competencies') }}
        </button>
      </div>
    </div>

    <template v-if="errorMessages">
      <div v-for="(error, index) in errorMessages" :key="index" class="card auto-comp-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </div>
    </template>
    <div v-if="successMessage" class="card auto-comp-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: green">
          <i class="uil-check"></i> {{ successMessage }}
        </p>
      </div>
    </div>

    <div class="card auto-comp-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 auto-comp-toolbar">
        <div class="d-flex justify-content-between auto-comp-toolbar-inner">
          <div class="auto-comp-search">
            <div class="uk-search uk-search-navbar auto-comp-search-field">
              <span class="uk-search-icon auto-comp-search-icon" aria-hidden="true">
                <i class="uil-search"></i>
              </span>
              <input v-model="filter.search" :placeholder="$t('general.search')"
                :aria-label="$t('general.search')"
                class="uk-search-input shadow-0 uk-form-small" type="search" />
            </div>
          </div>
          <div class="d-flex auto-comp-toolbar-actions">
            <sort-dropdown :sort-options="sortOption" :title="sortTitle" @sort-selected="sortOrder" />
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="sf-table auto-comp-table-wrap">
        <div v-if="programs_loading" class="text-center p-4">
          <default-loading />
        </div>
        <table v-else>
          <thead>
            <tr>
              <th scope="col">{{ $t('general.auto_competency_identification_name') }}</th>
              <th scope="col">{{ $t("general.added_at") }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-if="formattedItems.length === 0" class="text-center">
              <td class="sf-table__empty" colspan="3">
                <div class="text-muted">
                  <i class="uil-inbox" style="font-size: 2rem;"></i>
                  <p class="mt-2"> {{ $t('general.no_have_competency') }} </p>
                </div>
              </td>
            </tr>
            <tr v-for="item in formattedItems" :key="item.id">
              <td>{{ item.name }}</td>
              <td>{{ item.created_at }}</td>
              <td class="sf-table__col-actions">
                <div class="sf-table__actions auto-comp-row-actions">
                  <router-link :to="{
                    name: 'edit.automatic.competency',
                    params: { id: item.id },
                  }" :uk-tooltip="$t('faq_categories.edit')" class="btn btn-icon btn-hover btn-sm btn-circle">
                    <i class="uil-edit "></i>
                  </router-link>
                  <a aria-expanded="false" class="btn btn-icon btn-hover btn-sm btn-circle" href="javascript:void(0)"
                    :uk-tooltip="!$hasPermission('delete_automaticcompetency') ? $t('general.no_permission') : $t('general.delete')"
                    @click="$hasPermission('delete_automaticcompetency') && removeItem(item.id)" :style="{
                      cursor: $hasPermission('delete_automaticcompetency') ? 'pointer' : 'not-allowed',
                      pointerEvents: $hasPermission('delete_automaticcompetency') ? 'auto' : 'none'
                    }">
                    <i class="uil-minus-circle text-danger"></i>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
      @changePage="setPage($event)"></pagination>
  </main>
</template>

<script>
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";

import module, {
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  LOADING
} from "@/core/services/store/assignment.module";
import moment from "moment";

import _ from 'lodash';
import { confirmDialogDelete } from "@/utils/confirmDialog";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";

export default {
  name: "AssignmentAuto",
  components: { SortDropdown, Pagination, DefaultLoading, CompanyBreadcrumb },
  data() {
    return {
      pagination: null,
      activeItem: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 10,
      filter: {
        search: null,
        order: "desc",
        sort: "id",
      },
      sortTitle: this.$t("filters.order.sort"),
      sortOption: [
        { key: 'text-asc', sort: 'asc', order: 'id', label: this.$t("sorting.a_z") },
        { key: 'text-desc', sort: 'desc', order: 'id', label: this.$t("sorting.z_a") },
        { key: 'created_at-asc', sort: 'asc', order: 'created_at', label: this.$t("sorting.created_at_asc") },
        { key: 'created_at-desc', sort: 'desc', order: 'created_at', label: this.$t("sorting.created_at_desc") },
      ],
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("general.automatic_competency_definitions") },
      ];
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) {
      },
    },
    programs_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) {
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) {
      },
    },
    formattedItems() {
      let results = [];
      if (this.items === null || this.items.length === 0) return [];
      this.pagination = this.items;

      this.items.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          name: currentItem.name,
          description: currentItem.description ? currentItem.description : "-",
          total_section: currentItem.sections_count,
          total_content: currentItem.contents_count,
          created_at: currentItem.created_at
            ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "-",
        });
      });

      return results;
    },
  },
  methods: {
    getResult() {
      let self = this;
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: 'api/automatic-competencies',
        filters: {
          like: self.filter.search,
          page: self.page,
          perPage: self.per_page,
          sort: self.filter.sort,
          order: self.filter.order,
        },
      });
    },
    async removeItem(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        this.$store
          .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
            url: 'api/automatic-competencies/' + id,
          })
          .then(() => {
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_deleted");
              this.getResult();
            } else this.errorMessages.push(this.error);
          });
      }
    },
    sortOrder({ sort, order }) {
      this.page = 1;
      this.filter.sort = sort;
      this.filter.order = order;
      this.getResult();
    },
    setPage(page) {
      this.page = page;
      this.getResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    this.getResult();
  },
  watch: {
    "filter.search": function (val) {
      this.debouncedFilterSearch();
    },
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>

<style scoped>
.auto-comp-page {
  --auto-comp-primary: var(--primaryColor, #3e416d);
  --auto-comp-text: #1e293b;
  --auto-comp-muted: #64748b;
  --auto-comp-border: #e2e8f0;
  min-width: 0;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.auto-comp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.auto-comp-title {
  margin: 0;
  color: var(--auto-comp-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.auto-comp-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auto-comp-action-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.auto-comp-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--auto-comp-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.auto-comp-page .uk-alert {
  margin: 0;
}

.auto-comp-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  margin-top: 0;
  background: #fff;
  border: 1px solid var(--auto-comp-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.auto-comp-toolbar {
  padding: 16px;
}

.auto-comp-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.auto-comp-search {
  flex: 1 1 auto;
  min-width: 0;
}

.auto-comp-search-field,
.auto-comp-search-field.uk-search-navbar {
  display: block;
  position: relative;
  width: 100%;
  max-width: 100%;
}

.auto-comp-search-icon,
.auto-comp-page ::v-deep .auto-comp-search-icon.uk-search-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  margin: 0;
  pointer-events: none;
  color: #3f87f5;
  font-size: 20px;
  line-height: 1;
}

.auto-comp-search-icon i.uil-search {
  position: static;
  top: auto;
  left: auto;
  font-size: 20px;
  line-height: 1;
}

.auto-comp-search .uk-search-input {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 16px 10px 40px !important;
  border: 1px solid #cfd5df;
  border-radius: 10px;
  background: #fff;
  color: var(--auto-comp-text);
  font-size: 14px !important;
  line-height: 1.4;
}

.auto-comp-search .uk-search-input:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.25);
  outline-offset: 2px;
}

.auto-comp-toolbar-actions {
  align-items: center;
  gap: 8px;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Bu sayfada zebra YOK, satir alt cizgisi kullaniliyor — sistemin
   varsayilani da bu, bu yuzden --zebra modifier'i eklenmedi.
   Tabloda min-width de yok; 3 kolon mobilde sigdigi icin yatay
   kaydirma zorlanmiyor. */

.auto-comp-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.auto-comp-row-actions .btn-circle {
  width: 30px;
  height: 30px;
}

.auto-comp-action-btn:active,
.auto-comp-row-actions .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

@media (max-width: 959px) {
  .auto-comp-page {
    padding: 0 0 24px;
  }

  .auto-comp-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .auto-comp-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.4;
  }

  .auto-comp-header-actions {
    flex-direction: column;
    gap: 8px;
  }

  .auto-comp-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .auto-comp-toolbar-inner {
    flex-direction: column;
    align-items: stretch !important;
  }

  .auto-comp-search {
    width: 100%;
  }

  .auto-comp-toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .auto-comp-search-icon,
  .auto-comp-page ::v-deep .auto-comp-search-icon.uk-search-icon {
    width: 48px;
  }

  .auto-comp-search .uk-search-input {
    width: 100%;
    height: 48px;
    min-height: 48px;
    padding: 12px 16px 12px 48px !important;
    font-size: 16px !important;
    line-height: 1.4;
  }

  .auto-comp-row-actions .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }
}

@media (max-width: 767px) {
  .auto-comp-page {
    padding: 0 0 24px;
  }

  .auto-comp-card {
    border-radius: 14px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .auto-comp-action-btn:hover,
  .auto-comp-row-actions .btn-circle:hover {
    cursor: pointer;
  }
}

@media (hover: none) {
  .auto-comp-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auto-comp-action-btn:active,
  .auto-comp-row-actions .btn-circle:active {
    transform: none;
  }
}
</style>
